import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import clsx from 'clsx';

import VoiceRecordButton from '../voice-record-button/VoiceRecordButton';
import { useSpeechRecognition } from 'react-speech-recognition';

import './CommonTextarea.scss';

const CommonTextarea = ({
  extraInputClass,
  name,
  value = '',
  debounce = false,
  timeout = 500,
  disabled = false,
  onChange: onChangeProps,
  onBlur: onBlurProps,
  onFocus: onFocusProps,
  placeholder = '',
  autoFocus = false,
  tabIndex,
  isError = false,
  maxWidth,
  onClick,
  isReadOnly = false,
  autoComplete,
  rows = '2',
  resize = 'vertical',
  disableScroll = false,
  onEnter,
  enableVoiceInput = false,
  ...rest
}) => {
  const rootClassName = 'common-textarea';
  const inlineStyles = { maxWidth, resize };
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef();

  const voiceId = useId();

  const { browserSupportsSpeechRecognition } = useSpeechRecognition();

  const isVoiceEnabled = enableVoiceInput && browserSupportsSpeechRecognition && !isReadOnly;

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  const handleOnChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);

      if (!onChangeProps) {
        return;
      }

      if (debounce) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        timerRef.current = window.setTimeout(() => {
          onChangeProps(newValue);
        }, timeout);

        return;
      }

      onChangeProps(newValue);
    },
    [onChangeProps, debounce, timeout],
  );

  const handleVoiceChange = useCallback(
    (newValue) => {
      setLocalValue(newValue);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      onChangeProps?.(newValue);
    },
    [onChangeProps],
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && onEnter) {
      e.preventDefault();
      onEnter();
    }
  };

  return (
    <div className={clsx(`${rootClassName}_wrapper`)}>
      <textarea
        id={name}
        name={name}
        value={localValue}
        rows={rows}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={handleOnChange}
        onBlur={onBlurProps}
        disabled={disabled}
        tabIndex={tabIndex}
        readOnly={isReadOnly}
        autoComplete={autoComplete}
        onClick={handleClick}
        onFocus={onFocusProps}
        onKeyDown={handleKeyDown}
        className={clsx(
          `${rootClassName}_field`,
          isError && `${rootClassName}_field--error`,
          disableScroll && `${rootClassName}_field--disable-scroll`,
          isVoiceEnabled && `${rootClassName}_field--with-voice`,
          extraInputClass,
        )}
        style={inlineStyles}
        {...rest}
      />

      {isVoiceEnabled && (
        <div className={`${rootClassName}_voice`}>
          <VoiceRecordButton id={voiceId} value={localValue} onChange={handleVoiceChange} disabled={disabled} />
        </div>
      )}
    </div>
  );
};

export default CommonTextarea;
